/**
 * Client-side Embedded Signup message parsing. Isomorphic (no server deps) so it
 * is unit-testable without pulling the connect server action.
 */

/**
 * `extras.featureType` value that turns the WABA-selection screen into one
 * offering "connect an account you already use in the WhatsApp Business app"
 * (Coexistence). Sits beside `setup`, not inside it.
 */
export const WHATSAPP_COEXISTENCE_FEATURE_TYPE = 'whatsapp_business_app_onboarding'

/**
 * Which onboarding screen the user finished on. A hint for logging only — the
 * server decides whether a number is coexistence by querying Graph, because the
 * client cannot be trusted with an authorization branch.
 */
export type EmbeddedSignupFlow = 'standard' | 'business_app'

/** The asset ids Embedded Signup posts on completion (before the code arrives). */
export interface EmbeddedSignupAssets {
  /** Absent on the coexistence flow — Meta's payload carries only `waba_id`. */
  phoneNumberId: string | null
  wabaId: string
  flow: EmbeddedSignupFlow
}
/**
 * Whether a postMessage origin is facebook.com itself or one of its subdomains.
 * A bare `endsWith("facebook.com")` would also accept e.g. "evilfacebook.com".
 */
export function isTrustedEmbeddedSignupOrigin(origin: string): boolean {
  try {
    const { hostname } = new URL(origin)
    return hostname === 'facebook.com' || hostname.endsWith('.facebook.com')
  } catch {
    return false
  }
}

/** Finish events we treat as a completed signup, mapped to their flow. */
const FINISH_EVENTS: Record<string, EmbeddedSignupFlow> = {
  FINISH: 'standard',
  FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING: 'business_app',
}

/**
 * Parse a postMessage from the Embedded Signup popup. Returns the assets only
 * for a successful finish event, else null. The popup may deliver the payload as
 * an object or a JSON string.
 *
 * `waba_id` is the one required id — it is the routing key, and the coexistence
 * payload carries nothing else. The phone number is resolved server-side when
 * absent.
 */
export function parseEmbeddedSignupMessage(raw: unknown): EmbeddedSignupAssets | null {
  let data: unknown = raw
  if (typeof raw === "string") {
    try {
      data = JSON.parse(raw)
    } catch {
      return null
    }
  }
  if (!data || typeof data !== "object") return null
  const msg = data as {
    type?: string
    event?: string
    data?: { phone_number_id?: string; waba_id?: string }
  }
  if (msg.type !== "WA_EMBEDDED_SIGNUP") return null
  // hasOwn, or an event named "constructor"/"toString" resolves up the prototype
  // chain to a truthy non-flow value.
  const flow = msg.event && Object.hasOwn(FINISH_EVENTS, msg.event) ? FINISH_EVENTS[msg.event] : undefined
  if (!flow) return null
  const wabaId = msg.data?.waba_id
  if (!wabaId) return null
  return { phoneNumberId: msg.data?.phone_number_id || null, wabaId, flow }
}
